import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { HelpCircle, ChevronRight } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api } from "../lib/api";
import { useToast } from "../components/Toast";
import InstallGuideModal, { canShowInstallGuide } from "../components/InstallGuideModal";
import { getPendingDemoBonus, clearPendingDemoBonus } from "../components/DemoFlow";

export default function Register() {
  const { register, refresh } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referral, setReferral] = useState("");
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  useEffect(() => {
    const ref = searchParams.get("ref");
    if (ref) setReferral(ref.toUpperCase());
  }, [searchParams]);

  const finishRegister = async () => {
    await refresh();
    navigate("/", { replace: true });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setErr(null);
    if (!name || !email || !password) {
      setErr("All fields are required.");
      return;
    }
    if (password.length < 6) {
      setErr("Password must be at least 6 characters.");
      return;
    }
    setLoading(true);
    try {
      await register(email.trim(), password, name.trim(), referral.trim().toUpperCase() || null);

      // If they completed the guest demo, claim the cash they won during it.
      const pending = getPendingDemoBonus();
      if (pending) {
        try {
          const res = await api.claimDemoBonus(pending);
          toast.success(`Your demo winnings of £${res.amount.toFixed(2)} have been credited!`);
        } catch (e) {
          // Non-fatal: still let them through the rest of the flow.
          console.warn("Demo bonus claim failed", e);
        } finally {
          clearPendingDemoBonus();
          await refresh();
        }
      }

      // After signup: show install guide (if eligible), then land on Home.
      if (canShowInstallGuide()) {
        setShowGuide(true);
      } else {
        await finishRegister();
      }
    } catch (e) {
      setErr(e?.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const onGuideClose = async () => {
    setShowGuide(false);
    await finishRegister();
  };

  return (
    <div className="page-no-tab" style={{ paddingTop: 48 }}>
      <h1 style={{ fontSize: 38, fontWeight: 800, letterSpacing: -1.2, margin: 0, lineHeight: 1.05 }}>
        Create your <span style={{ color: "var(--primary-dark)" }}>account.</span>
      </h1>
      <p style={{ color: "var(--text-secondary)", fontSize: 15, marginTop: 8, marginBottom: 16 }}>
        Snap receipts. Get rewarded. Cash out from £10.
      </p>

      <Link
        to="/how-it-works"
        data-testid="register-how-it-works-link"
        style={{
          display: "flex", alignItems: "center", gap: 10,
          background: "var(--burgundy)",
          border: "1px solid var(--burgundy)",
          borderRadius: 14,
          padding: "12px 14px",
          marginBottom: 24,
          textDecoration: "none",
          color: "#FFFFFF",
          minHeight: 48,
          boxShadow: "0 14px 28px -18px rgba(216, 91, 91, 0.6)",
          WebkitTapHighlightColor: "transparent",
        }}
      >
        <span style={{
          width: 30, height: 30, borderRadius: 999, flexShrink: 0,
          background: "var(--primary)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 6px 14px -6px rgba(247,122,42,0.7)",
        }}>
          <HelpCircle size={15} color="#FFFFFF" />
        </span>
        <span style={{ flex: 1, fontSize: 13.5, fontWeight: 800, lineHeight: 1.3, color: "#FFFFFF" }}>
          New here? See how it works
        </span>
        <ChevronRight size={18} color="#FFFFFF" style={{ flexShrink: 0 }} />
      </Link>

      <form onSubmit={onSubmit}>
        <div style={{ marginBottom: 14 }}>
          <label className="label">Name</label>
          <input
            data-testid="register-name-input"
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Your name"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label className="label">Email</label>
          <input
            data-testid="register-email-input"
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            autoCapitalize="none"
            autoComplete="email"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label className="label">Password</label>
          <input
            data-testid="register-password-input"
            type="password"
            className="input"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters"
            autoComplete="new-password"
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label className="label">Referral code <span style={{ color: "var(--text-muted)", fontWeight: 600, letterSpacing: 0 }}>(optional)</span></label>
          <input
            data-testid="register-referral-input"
            className="input"
            value={referral}
            onChange={(e) => setReferral(e.target.value.toUpperCase())}
            placeholder="e.g. AB1C23"
            autoCapitalize="characters"
            maxLength={12}
            style={{ letterSpacing: 2, fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace" }}
          />
        </div>

        {err && (
          <div data-testid="register-error" style={{ color: "var(--error)", fontSize: 14, marginBottom: 12 }}>
            {err}
          </div>
        )}

        <button
          data-testid="register-submit-button"
          className="btn-primary"
          type="submit"
          disabled={loading}
          style={{ width: "100%", marginTop: 12 }}
        >
          {loading ? "Creating…" : "Create Account"}
        </button>
      </form>

      <div style={{ display: "flex", justifyContent: "center", marginTop: 24, color: "var(--text-secondary)" }}>
        <span>Already have an account?&nbsp;&nbsp;</span>
        <Link to="/login" data-testid="goto-login-link" style={{ color: "var(--primary)", fontWeight: 700 }}>
          Sign in
        </Link>
      </div>

      <InstallGuideModal open={showGuide} onClose={onGuideClose} />
    </div>
  );
}
